export const ROOT_CATEGORY_TAG_KEY = /^(?:investor|advisor):\d+$/;

const NUMERIC_INVESTOR_TAG_KEY = /^\d+$/;

export interface AccountGroupTagOption {
  /** Segment `InvestorTagId` from the tag catalog. */
  value: number;
  label: string;
  /** Parent account-group category id (`TagCategoryId`). */
  categoryId?: number;
  isAdvisorTag?: boolean;
}

export function accountGroupTagStorageKey(option: {
  categoryId: number;
  isAdvisorTag?: boolean;
}): string {
  const prefix = option.isAdvisorTag ? 'advisor' : 'investor';
  return `${prefix}:${String(option.categoryId)}`;
}

function accountTagSelectionSlotKey(option: AccountGroupTagOption): string {
  if (option.categoryId != null) {
    return accountGroupTagStorageKey({
      categoryId: option.categoryId,
      isAdvisorTag: option.isAdvisorTag,
    });
  }
  return `user:${String(option.value)}`;
}

/**
 * Keeps at most one account-group segment per category slot
 * (`investor:<categoryId>` / `advisor:<categoryId>`). Personal tags (no
 * `categoryId`) are all kept. Later selections replace earlier ones for the
 * same slot so the picker matches save behavior.
 */
export function normalizeAccountTagSelection(
  selectedOptions: readonly AccountGroupTagOption[],
): AccountGroupTagOption[] {
  const lastByKey = new Map<string, AccountGroupTagOption>();
  const order: string[] = [];

  for (const option of selectedOptions) {
    const key = accountTagSelectionSlotKey(option);
    if (!lastByKey.has(key)) {
      order.push(key);
    }
    lastByKey.set(key, option);
  }

  return order.flatMap((key) => {
    const option = lastByKey.get(key);
    return option ? [option] : [];
  });
}

/**
 * Resolves selected IDs against the catalog and keeps at most one segment per
 * account-group slot. IDs missing from `options` are omitted.
 */
export function selectedAccountTagOptions(
  options: readonly AccountGroupTagOption[],
  selectedTagIds: readonly number[],
): AccountGroupTagOption[] {
  const selectedById = new Map(options.map((tag) => [tag.value, tag] as const));
  return normalizeAccountTagSelection(
    selectedTagIds.flatMap((id) => {
      const option = selectedById.get(id);
      return option ? [option] : [];
    }),
  );
}

/**
 * Maps persisted `account.TagIds` onto catalog ids for the account tag picker.
 * Legacy `account.Tags` entries are not an input: numeric keys and
 * `investor:` / `advisor:` category keys belong to AccountTags and custom
 * groups, and their ids can collide with System A tag ids.
 */
export function deriveSelectedInvestorTagIdsFromAccount(
  tagIds: number[] | undefined,
  allTags: AccountGroupTagOption[],
): number[] {
  if (!tagIds?.length || allTags.length === 0) {
    return [];
  }
  const catalogIds = new Set(allTags.map((tag) => tag.value));
  const selected: number[] = [];
  const seen = new Set<number>();
  for (const tagId of tagIds) {
    if (!Number.isFinite(tagId) || seen.has(tagId) || !catalogIds.has(tagId)) {
      continue;
    }
    seen.add(tagId);
    selected.push(tagId);
  }
  return selected;
}

/**
 * Tag ids to send on an account save. The picker only edits tags in
 * `assignableTagIds`. Ids already on the account that the user cannot assign
 * stay on the payload so a tag edit does not drop them. Assignable ids the
 * user cleared are omitted.
 */
export function tagIdsForAccountSave(
  selectedTagIds: readonly number[],
  assignableTagIds: ReadonlySet<number>,
  existingTagIds: readonly number[] | undefined,
): number[] {
  const selected = selectedTagIds.filter((id) => assignableTagIds.has(id));
  const selectedSet = new Set(selected);
  const preserved = (existingTagIds ?? []).filter(
    (id) =>
      Number.isFinite(id) && !assignableTagIds.has(id) && !selectedSet.has(id),
  );
  return [...selected, ...preserved];
}

function readExistingTagValue(
  tags: Record<string, string>,
  key: string,
): string | undefined {
  return Object.prototype.hasOwnProperty.call(tags, key)
    ? tags[key]
    : undefined;
}

function hasPriorAssignmentForCategory(
  base: Record<string, string>,
  categoryKey: string,
  allTags: AccountGroupTagOption[],
): boolean {
  const existing = readExistingTagValue(base, categoryKey);
  if (existing !== undefined && existing !== '') {
    return true;
  }

  return allTags.some((tag) => {
    if (tag.categoryId == null) {
      return false;
    }
    const tagCategoryKey = accountGroupTagStorageKey({
      categoryId: tag.categoryId,
      isAdvisorTag: tag.isAdvisorTag,
    });
    if (tagCategoryKey !== categoryKey) {
      return false;
    }
    const legacyMiskeyed = readExistingTagValue(
      base,
      `investor:${String(tag.value)}`,
    );
    if (legacyMiskeyed !== undefined && legacyMiskeyed !== '') {
      return true;
    }
    const legacyNumeric = readExistingTagValue(base, String(tag.value));
    return legacyNumeric !== undefined && legacyNumeric !== '';
  });
}

/**
 * Builds the account `Tags` map for save. Account-group assignments use
 * `investor:<TagCategoryId>` / `advisor:<TagCategoryId>`, matching
 * AssignAccountSegmentsModal, formatGroups, and portfolio grouping.
 *
 * Cleared assignments are persisted as empty strings so the API removes
 * group membership (legacy Account Grouping behavior).
 */
export function buildAccountGroupTagsForSave(
  baseTags: Record<string, string> | undefined,
  selectedTagIds: number[],
  allTags: AccountGroupTagOption[],
): Record<string, string> {
  const out: Record<string, string> = {};
  const base = baseTags ?? {};

  const managedCategoryKeys = new Set(
    allTags
      .filter((tag) => tag.categoryId != null)
      .map((tag) =>
        accountGroupTagStorageKey({
          categoryId: tag.categoryId as number,
          isAdvisorTag: tag.isAdvisorTag,
        }),
      ),
  );
  const legacyMiskeyedCategoryKeys = new Set(
    allTags
      .filter((tag) => tag.categoryId != null)
      .map((tag) => `investor:${String(tag.value)}`),
  );
  const legacyNumericInvestorTagIds = new Set(
    allTags.map((tag) => String(tag.value)),
  );

  for (const [key, value] of Object.entries(base)) {
    const isLegacyNumericKey =
      NUMERIC_INVESTOR_TAG_KEY.test(key) &&
      legacyNumericInvestorTagIds.has(key);
    if (
      !isLegacyNumericKey &&
      !managedCategoryKeys.has(key) &&
      !legacyMiskeyedCategoryKeys.has(key)
    ) {
      out[key] = value;
    }
  }

  const selectedByCategoryKey = new Map<string, string>();
  for (const investorTagId of selectedTagIds) {
    const option = allTags.find((tag) => tag.value === investorTagId);
    if (option?.categoryId != null) {
      selectedByCategoryKey.set(
        accountGroupTagStorageKey({
          categoryId: option.categoryId,
          isAdvisorTag: option.isAdvisorTag,
        }),
        option.label,
      );
    }
  }

  for (const [key, label] of selectedByCategoryKey) {
    out[key] = label;
  }

  for (const categoryKey of managedCategoryKeys) {
    if (selectedByCategoryKey.has(categoryKey)) {
      continue;
    }
    if (hasPriorAssignmentForCategory(base, categoryKey, allTags)) {
      // Legacy behavior: persist empty string to explicitly clear assignment
      out[categoryKey] = '';
    }
  }

  return out;
}

/**
 * Applies account-group tag assignments to a PUT account payload the way the
 * legacy API expects: keep them in `Tags`, and flatten investor:/advisor:
 * category keys onto the root object.
 *
 * Mutates `payload` in place and returns it for convenient chaining.
 */
export function applyAccountGroupTagsToAccountPayload<T extends object>(
  payload: T,
  tags: Record<string, string>,
): T {
  const flattenedCategoryTags = Object.fromEntries(
    Object.entries(tags).filter(([key]) => ROOT_CATEGORY_TAG_KEY.test(key)),
  );

  for (const key of Object.keys(payload)) {
    if (ROOT_CATEGORY_TAG_KEY.test(key)) {
      Reflect.deleteProperty(payload, key);
    }
  }

  Object.assign(payload, {
    Tags: tags,
    TagsWasUpdated: true,
    ...flattenedCategoryTags,
  });

  return payload;
}

/**
 * Updates a single account-group category assignment on an existing Tags map
 * (Account Grouping edit surface).
 */
export function setAccountGroupCategoryAssignment(
  baseTags: Record<string, string> | undefined,
  tagCategoryId: number,
  segmentName: string | undefined,
  options?: { isAdvisorTag?: boolean },
): Record<string, string> {
  const key = accountGroupTagStorageKey({
    categoryId: tagCategoryId,
    isAdvisorTag: options?.isAdvisorTag,
  });
  return {
    ...(baseTags ?? {}),
    [key]: segmentName ?? '',
  };
}
